import React, { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { apiGet, apiDel } from '../lib/api';
import { inrCompact, formatDate, statusColor } from '../lib/format';
import {
  TrendingUp, TrendingDown, AlertTriangle, Clock, Users, ShoppingCart,
  ShieldAlert, Package, ArrowUpRight, Activity, BarChart2, Trash2
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { DateRangePicker } from "../components/DateRangePicker";
import CompanyKpiStrip from "../components/CompanyKpiStrip";
import CommandCenter from "../components/CommandCenter";
import { toast } from 'sonner';

const KpiCard = ({ overline, value, sub, icon: Icon, accent, trend }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-5 hover:border-[#3b4872]/40 transition-colors">
    <div className="flex items-start justify-between mb-3">
      <div className="kpi-overline">{overline}</div>
      <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${accent || 'bg-slate-100 text-slate-600'}`}>
        <Icon size={15} />
      </div>
    </div>
    <div className="kpi-value">{value}</div>
    {sub && (
      <div className="mt-2 text-xs text-slate-500 flex items-center gap-1">
        {trend === 'up' && <TrendingUp size={12} className="text-emerald-600" />}
        {trend === 'down' && <TrendingDown size={12} className="text-red-600" />}
        <span>{sub}</span>
      </div>
    )}
  </div>
);

const STATUS_COLORS = { Active: '#10B981', Delayed: '#EF4444', Completed: '#94A3B8' };

export default function Dashboard() {
  const ctx = useOutletContext() || {};
  const activeProject = ctx.activeProject || '';
  const projectsList = ctx.projects || [];
  const activeProjectName = activeProject
    ? (projectsList.find(p => p.id === activeProject)?.name || '')
    : '';
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);
  // Default = This Month
  const _today = new Date();
  const _monthStart = new Date(_today.getFullYear(), _today.getMonth(), 1);
  const [dateRange, setDateRange] = useState({ from: _monthStart, to: _today });
  const [presetLabel, setPresetLabel] = useState('This Month');
  const [activities] = useState([
    { type: 'PO', text: 'PO ELR/PO/2026/0143 (Tata Steel) submitted for approval', time: '2h ago', tag: 'Procurement' },
    { type: 'DPR', text: 'DPR for Mumbai Coastal — Pier P-23 reinforcement complete', time: '4h ago', tag: 'DPR' },
    { type: 'NCR', text: 'NCR-026 raised: Concrete strength below spec on Pier P-22', time: '6h ago', tag: 'Quality' },
    { type: 'Invoice', text: 'UltraTech Cement invoice ₹ 1.25 Cr awaiting Director approval', time: '1d ago', tag: 'Finance' },
    { type: 'Safety', text: 'Toolbox Talk: Working at Heights — 48 attendees', time: '1d ago', tag: 'HSE' },
  ]);

  const applyPreset = (label) => {
    const today = new Date();
    let from = null;
    let to = today;
    switch (label) {
      case 'This Month':
        from = new Date(today.getFullYear(), today.getMonth(), 1); break;
      case 'Last Month': {
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0); break;
      }
      case 'Last 3 Months':
        from = new Date(today.getFullYear(), today.getMonth() - 3, 1); break;
      case 'Last 6 Months':
        from = new Date(today.getFullYear(), today.getMonth() - 6, 1); break;
      case 'Last 1 Year':
        from = new Date(today.getFullYear() - 1, today.getMonth(), 1); break;
      case 'All Time':
        from = new Date(2000, 0, 1); break;
      default:
        from = new Date(today.getFullYear(), today.getMonth(), 1);
    }
    setDateRange({ from, to });
    setPresetLabel(label);
  };

  useEffect(() => {
    setData(null);
    const params = new URLSearchParams();
    if (activeProject) params.set('project_id', activeProject);
    if (dateRange?.from && dateRange?.to) {
      params.set('start_date', dateRange.from.toISOString());
      params.set('end_date', dateRange.to.toISOString());
    }
    apiGet(`/dashboard/summary?${params}`).then(setData).catch(() => {});
  }, [activeProject, dateRange]);

  useEffect(() => {
    apiGet('/reports/builder/templates').then(d => setSavedTemplates(d?.items || [])).catch(() => {});
  }, []);

  const deleteTemplate = async (id, e) => {
    e.stopPropagation();
    await apiDel(`/reports/builder/templates/${id}`);
    setSavedTemplates(prev => prev.filter(t => t.id !== id));
    toast.success('Template deleted');
  };

  if (!data) {
    return <div className="p-8 text-sm text-slate-500 font-mono">Loading dashboard…</div>;
  }
  const k = data.kpis;
  const projects = data.projects || [];
  const pieData = [
    { name: 'Active', value: k.active_projects },
    { name: 'Delayed', value: k.delayed_projects },
    { name: 'Completed', value: k.completed_projects },
  ];

  return (
    <div>
      {/* CEO Command Center — current-state KPIs, NOT affected by the period
          selector below. Renders nothing for non-exec users (API 403). */}
      <CommandCenter />

      {/* ── Custom Report Builder entry point ─────────────────────────── */}
      <div className="mb-6 bg-white border border-slate-200 rounded-sm px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-sm bg-[#3b4872]/10 text-[#3b4872] flex items-center justify-center">
            <BarChart2 size={18} />
          </div>
          <div>
            <div className="font-display font-bold text-sm">Custom Report Builder</div>
            <div className="text-[11px] text-slate-500">Pick any data domain, add filters, group, export to Excel</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {savedTemplates.map(t => (
            <div key={t.id} className="flex items-center gap-1 border border-slate-200 rounded-sm">
              <button onClick={() => navigate(`/app/reports/builder?template=${t.id}`)}
                className="text-[11px] px-3 py-1.5 hover:bg-slate-50 font-display text-[#3b4872]"
                data-testid={`template-quick-${t.id}`}>
                {t.name}
              </button>
              <button onClick={e => deleteTemplate(t.id, e)} className="px-1.5 py-1.5 text-slate-400 hover:text-rose-500">
                <Trash2 size={11} />
              </button>
            </div>
          ))}
          <button onClick={() => navigate('/app/reports/builder')}
            className="bg-[#3b4872] text-white px-4 py-2 rounded-sm font-display font-bold text-xs flex items-center gap-1.5 hover:bg-[#2d3856]"
            data-testid="open-report-builder-btn">
            <BarChart2 size={12} /> Build a Custom Report
          </button>
        </div>
      </div>

      <div className="mb-7">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Executive Dashboard</div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
              {activeProjectName ? activeProjectName : 'Project Command Center'}
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              {activeProject
                ? `Drill-down view · ${presetLabel} · ${k.workforce}+ workforce on this project.`
                : `Real-time overview across ${k.total_projects} projects, ${k.workforce}+ workforce, ₹ ${(k.total_budget/10000000).toFixed(0)} Cr live contract value.`}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap" data-testid="dashboard-period-selector">
            {/* Preset Pills */}
            <div className="inline-flex flex-wrap gap-1 bg-slate-50 p-1 rounded-sm border border-slate-200">
              {['This Month', 'Last Month', 'Last 3 Months', 'Last 6 Months', 'Last 1 Year', 'All Time'].map(label => (
                <button
                  key={label}
                  onClick={() => applyPreset(label)}
                  className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-display font-bold rounded-sm transition-colors ${presetLabel === label ? 'bg-[#3b4872] text-white' : 'text-slate-600 hover:bg-white'}`}
                  data-testid={`preset-${label.replace(/\s/g, '-').toLowerCase()}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* Custom Range Picker */}
            <DateRangePicker date={dateRange} setDate={(d) => { setDateRange(d); setPresetLabel('Custom'); }} />
          </div>
        </div>
      </div>

      {/* Multi-Company Snapshot (executive only; hidden for non-execs by API 403) */}
      <CompanyKpiStrip />

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" key={`${presetLabel}-${activeProject}`}>
        <KpiCard overline="Active Projects" value={k.active_projects} sub={`${k.delayed_projects} delayed · ${k.completed_projects} done`} icon={Activity} accent="bg-indigo-50 text-[#3b4872]" />
        <KpiCard overline="Period Spent" value={`${k.budget_utilization}%`} sub={`${inrCompact(k.total_spent)} of ${inrCompact(k.total_budget)}`} icon={TrendingUp} accent="bg-emerald-50 text-emerald-700" trend="up" />
        <KpiCard overline={`Workforce · ${presetLabel}`} value={k.workforce.toLocaleString('en-IN')} sub="Sites mobilized" icon={Users} accent="bg-blue-50 text-blue-700" />
        <KpiCard overline={`POs · ${presetLabel}`} value={k.pending_pos} sub="Awaiting approval" icon={ShoppingCart} accent="bg-amber-50 text-amber-700" />
        <KpiCard overline={`NCRs · ${presetLabel}`} value={k.open_ncrs} sub="Quality issues" icon={AlertTriangle} accent="bg-red-50 text-red-700" />
        <KpiCard overline={`Incidents · ${presetLabel}`} value={k.safety_incidents} sub="HSE log" icon={ShieldAlert} accent="bg-slate-100 text-slate-700" />
      </div>

      {/* Cash flow + Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="kpi-overline">Cash Flow</div>
              <h3 className="font-display font-bold text-lg mt-1">
                Budget vs Actual — {dateRange?.from && dateRange?.to 
                  ? `${dateRange.from.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${dateRange.to.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
                  : presetLabel}
              </h3>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#2F3E46]" /> Budgeted</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#3b4872]" /> Actual</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.cash_flow}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v/10000000).toFixed(0)}Cr`} />
              <Tooltip cursor={{ fill: 'rgba(255,87,34,0.06)' }} contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, fontSize: 12 }} formatter={(v) => `₹ ${(v/10000000).toFixed(2)} Cr`} />
              <Bar dataKey="budgeted" fill="#2F3E46" radius={[2,2,0,0]} />
              <Bar dataKey="actual" fill="#3b4872" radius={[2,2,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="kpi-overline">Portfolio Mix</div>
          <h3 className="font-display font-bold text-lg mt-1 mb-2">Project Status</h3>
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} strokeWidth={0}>
                {pieData.map((d, i) => <Cell key={i} fill={STATUS_COLORS[d.name]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-2">
            {pieData.map(d => (
              <div key={d.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5" style={{background: STATUS_COLORS[d.name]}} />
                  <span className="text-slate-600">{d.name}</span>
                </span>
                <span className="font-display font-bold">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Project Health Table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="kpi-overline">Project Health</div>
              <h3 className="font-display font-bold text-lg mt-1">Live Portfolio</h3>
            </div>
            <a href="/app/projects" className="text-xs font-display font-bold uppercase tracking-widest text-[#3b4872] hover:underline flex items-center gap-1">View all <ArrowUpRight size={12} /></a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead><tr>
                <th>Project</th><th>Client</th><th className="text-right">Progress</th><th className="text-right">Spent / Value</th><th>Risk</th><th>Status</th>
              </tr></thead>
              <tbody>
                {projects.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div className="font-display font-semibold text-[#0F172A] text-[13px]">{p.code}</div>
                      <div className="text-xs text-slate-500 truncate max-w-[180px]">{p.name}</div>
                    </td>
                    <td className="text-slate-600 text-xs">{p.client}</td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                          <div className="h-full" style={{ width: `${p.progress || 0}%`, background: p.progress >= 80 ? '#10B981' : p.progress >= 40 ? '#3b4872' : '#94A3B8' }} />
                        </div>
                        <span className="text-xs font-display font-bold tabular-nums w-8" title={p.progress_basis ? `Basis: ${p.progress_basis}` : ''}>{p.progress == null ? '—' : `${p.progress}%`}</span>
                      </div>
                    </td>
                    <td className="text-right">
                      <div className="font-display font-bold text-xs">{inrCompact(p.spent)}</div>
                      <div className="text-[10px] text-slate-500">/ {inrCompact(p.contract_value)}</div>
                    </td>
                    <td><span className={`tag ${statusColor(p.risk_level)}`}>{p.risk_level}</span></td>
                    <td><span className={`tag ${statusColor(p.status)}`}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="p-5 border-b border-slate-200">
            <div className="kpi-overline">Activity Feed</div>
            <h3 className="font-display font-bold text-lg mt-1">What's happening now</h3>
          </div>
          <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
            {activities.map((a, i) => (
              <div key={i} className="p-4 hover:bg-slate-50">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[9px] uppercase tracking-widest font-display font-bold px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-sm">{a.tag}</span>
                  <span className="text-[10px] text-slate-400 font-mono ml-auto">{a.time}</span>
                </div>
                <div className="text-xs text-slate-700 leading-relaxed">{a.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom alerts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div className="bg-white border-l-4 border-l-[#3b4872] border border-slate-200 rounded-sm p-5">
          <div className="kpi-overline mb-2 flex items-center gap-2"><Package size={11} /> Material Stock Alert</div>
          <div className="font-display text-xl font-bold mb-1">2 items below reorder</div>
          <div className="text-xs text-slate-600">TMT Bar 12mm (84 MT) and Diesel HSD (4,200 L) need replenishment.</div>
        </div>
        <div className="bg-white border-l-4 border-l-amber-500 border border-slate-200 rounded-sm p-5">
          <div className="kpi-overline mb-2 flex items-center gap-2"><Clock size={11} /> Pending Approvals</div>
          <div className="font-display text-xl font-bold mb-1">{k.pending_pos + 2} actions</div>
          <div className="text-xs text-slate-600">{k.pending_pos} POs · 1 invoice · 1 leave request awaiting your approval.</div>
        </div>
        <div className="bg-white border-l-4 border-l-emerald-500 border border-slate-200 rounded-sm p-5">
          <div className="kpi-overline mb-2 flex items-center gap-2"><ShieldAlert size={11} /> Safety Status</div>
          <div className="font-display text-xl font-bold mb-1">2,18,400 hrs</div>
          <div className="text-xs text-slate-600">Without LTI across all sites. Last incident: Near Miss, 5 days ago.</div>
        </div>
      </div>
    </div>
  );
}
